import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, TextField } from "@mui/material";
import { formatDate } from "../utils/formatDate";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Card,
  Fade,
  Stack,
  Select,
  MenuItem,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Container,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Assessment as AssessmentIcon,
  ThumbUp as ThumbUpIcon,
  Build as BuildIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Edit,
  Close as CloseIcon,
  Check,
  AddComment as AddCommentIcon,
  ThumbDown,
} from "@mui/icons-material";
import BookIcon from "@mui/icons-material/Book";

import type { JournalEntry } from "../types/index";
import { AddTLFeedback, UpdateJournalAction } from "../utils/APIRequest";

interface JournalListingProps {
  theme: any;
  navigate: (path: string) => void;
  viewFilter: string;
  setViewFilter: (val: string) => void;
  filteredEntries: JournalEntry[];
  handleAddJournal: () => void;
  handleEditJournal: (journal: JournalEntry) => void;
  getTeamLeadInitials: (name: string) => string;
  fadeInUp: any;
  slideInLeft: any;
  deleteJournal: (journalId: string) => void;
  isTeamJournal?: boolean;
}

const JournalListing: React.FC<JournalListingProps> = ({
  theme,
  navigate,
  viewFilter,
  setViewFilter,
  filteredEntries,
  handleAddJournal,
  handleEditJournal,
  getTeamLeadInitials,
  fadeInUp,
  slideInLeft,
  deleteJournal,
  isTeamJournal = false
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);
  // Local state for TL feedback input (per journal id)
  const [tlFeedbackInputs, setTLFeedbackInputs] = useState<{ [id: string]: string }>({});

  const handleDeleteClick = (journalId: string) => {
    setSelectedDeleteId(journalId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedDeleteId) {
      deleteJournal(selectedDeleteId);
    }
    setDeleteDialogOpen(false);
    setSelectedDeleteId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedDeleteId(null);
  };
  // Dialog state for TL feedback
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackDialogId, setFeedbackDialogId] = useState<string | null>(null);
  const [feedbackDialogValue, setFeedbackDialogValue] = useState('');

  // Filter for team journals: only show entries where teamLead matches logged-in user's name
  const displayEntries = filteredEntries;

  // Open dialog for TL feedback
  const handleOpenFeedbackDialog = (journalId: string) => {
    setFeedbackDialogId(journalId);
    setFeedbackDialogValue("");
    setFeedbackDialogOpen(true);
  };
  const handleCloseFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
    setFeedbackDialogId(null);
    setFeedbackDialogValue("");
  };
  const handleSaveFeedbackDialog = async () => {
    if (feedbackDialogId) {
      try {
        const response = await AddTLFeedback({ journalId: feedbackDialogId, tlFeedback: feedbackDialogValue });
        if (response && response.status === "success") {
          // Update the entry in local state
          const entry = displayEntries.find(e => e.id === feedbackDialogId);
          if (entry) {
            entry.tlFeedback = feedbackDialogValue;
          }
        }
      } catch (error) {
        // Optionally show error
      }
    }
    setFeedbackDialogOpen(false);
    setFeedbackDialogId(null);
    setFeedbackDialogValue("");
  };
  // Handle Approve/Reject actions
  // Force re-render state
  const [, setRefresh] = useState(0);
  const handleJournalAction = async (journalId: string, action: boolean) => {
    try {
      const response = await UpdateJournalAction(journalId, action);
      if (response && response.status === "success") {
        // Update the entry in local state and force re-render
        const entry = displayEntries.find((e: JournalEntry) => e.id === journalId);
        if (entry) {
          (entry as any).actions = action;
          setRefresh(r => r + 1);
        }
      }
    } catch (error) {
      // Optionally show error
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        py: 4,
      }}
    >

      <Container maxWidth="xl">
        <Fade in timeout={800}>
          <Box>
            {/* Filter and Add Button */}
            <Card
              sx={{
                p: 3,
                mb: 3,
                animation: `${slideInLeft} 0.6s ease-out 0.3s both`,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <FilterIcon sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ color: "text.primary" }}>
                    View by:
                  </Typography>
                  <Select
                    value={viewFilter}
                    onChange={(e) => setViewFilter(e.target.value)}
                    size="small"
                    sx={{
                      minWidth: 200,
                      "& .MuiOutlinedInput-notchedOutline": {
                        // borderColor: "rgba(122, 158, 62, 0.3)",
                      },
                    }}
                  >
                    <MenuItem value="This Year">This Year</MenuItem>
                    <MenuItem value="Last 3 months">Last 3 months</MenuItem>
                    <MenuItem value="Last 6 months">Last 6 months</MenuItem>
                  </Select>
                </Stack>

                {!isTeamJournal && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddJournal}
                    size="large"
                    sx={{
                      minWidth: 160,
                      boxShadow: "0 4px 14px rgba(122, 158, 62, 0.3)",
                    }}
                  >
                    Add Journal
                  </Button>
                )}
              </Stack>
            </Card>

            {/* Enhanced Table */}
            {displayEntries.length === 0 ? (
              <Fade in timeout={600}>
                <Paper
                  sx={{
                    p: 6,
                    textAlign: "center",
                    borderRadius: 3,
                    animation: `${fadeInUp} 0.6s ease-out 0.4s both`,
                  }}
                >
                  <AssessmentIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
                  <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 1 }}>
                    No Records Found
                  </Typography>
                </Paper>
              </Fade>
            ) : (
              <Fade in timeout={800}>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 1,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    animation: `${fadeInUp} 0.6s ease-out 0.4s both`,
                    background: theme.palette.background.paper,
                    width: '90vw',
                    maxWidth: '90vw',
                    margin: '0 auto',
                  }}
                >
                  <Table sx={{ minWidth: 1200, width: '90vw', background: theme.palette.background.paper, tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)` }}>
                        <TableCell sx={{ width: '7%', color: theme.palette.primary.contrastText, fontWeight: 700, fontSize: '1rem', border: 0 }}>Sl.No</TableCell>
                        <TableCell sx={{ width: '10%', color: theme.palette.primary.contrastText, fontWeight: 700, border: 0 }}>Date Of Journal</TableCell>
                        <TableCell sx={{ width: '10%', color: theme.palette.primary.contrastText, fontWeight: 700, border: 0 }}>{isTeamJournal ? 'Team' : 'Team Lead'}</TableCell>
                        <TableCell sx={{ width: '10%', color: theme.palette.primary.contrastText, fontWeight: 700, border: 0 }}>Type</TableCell>
                        <TableCell sx={{ width: '11%', color: theme.palette.primary.contrastText, fontWeight: 700, border: 0 }}>Category</TableCell>
                        <TableCell sx={{ width: '26%', color: theme.palette.primary.contrastText, fontWeight: 700, border: 0 }}>Employee Feedback</TableCell>
                        <TableCell sx={{ width: '18%', color: theme.palette.primary.contrastText, fontWeight: 700, border: 0 }}>TL Feedback</TableCell>
                        <TableCell sx={{ width: '8%', color: theme.palette.primary.contrastText, fontWeight: 700, border: 0 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayEntries.map((entry, index) => {
                        // TL feedback input handlers for Team Journals
                        const handleTLFeedbackChange = (val: string) => {
                          setTLFeedbackInputs(prev => ({ ...prev, [entry.id]: val }));
                        };
                        const handleTLSaveFeedback = () => {
                          // TODO: Replace with API call to save feedback
                          entry.tlFeedback = tlFeedbackInputs[entry.id] || '';
                          setTLFeedbackInputs(prev => ({ ...prev, [entry.id]: '' }));
                          // Optionally force re-render if needed
                        };
                        return (
                          <TableRow
                            key={entry.id}
                            hover
                            sx={{
                              background: index % 2 === 0 ? theme.palette.background.default : theme.palette.action.hover,
                              '&:hover': {
                                background: theme.palette.action.selected,
                              },
                              transition: "all 0.2s ease",
                              animation: `${slideInLeft} 0.5s ease-out ${index * 0.1}s both`,
                              height: "80px",
                            }}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "50%",
                                  backgroundColor: theme.palette.secondary.main,
                                  color: theme.palette.secondary.contrastText,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 700,
                                  fontSize: "1rem",
                                  margin: "0 auto",
                                }}
                              >
                                {index + 1}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="left" spacing={1} justifyContent="left">
                                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  {formatDate(entry.dateOfJournal)}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="column" alignItems="left" spacing={1}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem", color: theme.palette.text.primary }}>
                                  {isTeamJournal ? (entry.employeeName || '') : entry.teamLead}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="left" spacing={1} justifyContent="left">
                                {entry.feedbackType === "Positive" ? (
                                  <ThumbUpIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                                ) : (
                                  <ThumbDown sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: entry.feedbackType === "Positive" ? theme.palette.success.main : theme.palette.warning.main,
                                    fontWeight: 600,
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {entry.feedbackType}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={entry.feedbackCategory}
                                size="medium"
                                variant="outlined"
                                sx={{
                                  fontWeight: 600,
                                  borderColor: theme.palette.secondary.main,
                                  color: theme.palette.secondary.main,
                                  backgroundColor: theme.palette.secondary.light + '22',
                                  fontSize: "0.85rem",
                                  height: "30px",
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "left", padding: "16px" }}>
                              {entry.employeeFeedback && entry.employeeFeedback.length > 200 ? (
                                <Tooltip title={entry.employeeFeedback} placement="bottom" enterDelay={300}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 4,
                                      WebkitBoxOrient: "vertical",
                                      lineHeight: 1.4,
                                      color: theme.palette.text.primary,
                                      fontSize: "0.9rem",
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {entry.employeeFeedback.slice(0, 200)}...
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 5,
                                    WebkitBoxOrient: "vertical",
                                    lineHeight: 1.4,
                                    color: theme.palette.text.primary,
                                    fontSize: "0.9rem",
                                    cursor: 'pointer',
                                  }}
                                >
                                  {entry.employeeFeedback}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ textAlign: "left", padding: "16px" }}>
                              {entry.tlFeedback && entry.tlFeedback.length > 100 ? (
                                <Tooltip title={entry.tlFeedback} placement="bottom" enterDelay={300}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 4,
                                      WebkitBoxOrient: "vertical",
                                      lineHeight: 1.4,
                                      color: theme.palette.text.primary,
                                      fontSize: "0.9rem",
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {entry.tlFeedback.slice(0, 100)}...
                                  </Typography>
                                </Tooltip>
                              ) : entry.tlFeedback ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 4,
                                    WebkitBoxOrient: "vertical",
                                    lineHeight: 1.4,
                                    color: theme.palette.text.primary,
                                    fontSize: "0.9rem",
                                    cursor: 'pointer',
                                  }}
                                >
                                  {entry.tlFeedback}
                                </Typography>
                              ) : isTeamJournal ? (
                                <Tooltip title="Add TL Feedback">
                                  <IconButton color="primary" size="small" onClick={() => handleOpenFeedbackDialog(entry.id)}>
                                    <AddCommentIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontStyle: "italic",
                                    textAlign: "left",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  Awaiting feedback
                                </Typography>
                              )}
                            </TableCell>
                            {/* TL Feedback Dialog */}
                            <Dialog fullWidth open={feedbackDialogOpen} onClose={handleCloseFeedbackDialog}>
                              <DialogTitle>Add TL Feedback</DialogTitle>
                              <DialogContent>
                                <TextField
                                  autoFocus
                                  label="Enter feedback"
                                  multiline
                                  minRows={4}
                                  maxRows={10}
                                  fullWidth
                                  value={feedbackDialogValue}
                                  onChange={(e) => setFeedbackDialogValue(e.target.value)}
                                  variant="outlined"
                                  sx={{ mt: 2 }}
                                />
                              </DialogContent>
                              <DialogActions>
                                <Button onClick={handleCloseFeedbackDialog} color="primary">
                                  Cancel
                                </Button>
                                <Button onClick={handleSaveFeedbackDialog} color="primary" variant="contained" disabled={!feedbackDialogValue.trim()}>
                                  Save
                                </Button>
                              </DialogActions>
                            </Dialog>
                            <TableCell>
                              <Stack direction="row" spacing={1} justifyContent="left" alignItems="center">
                                {entry.actions === true ? (
                                  <Chip label="Approved" color="success" size="small" />
                                ) : entry.actions === false ? (
                                  <Chip label="Rejected" color="error" size="small" />
                                ) : isTeamJournal ? (
                                  <>
                                    <Tooltip title="Approve">
                                      <IconButton color="success" size="small" onClick={() => handleJournalAction(entry.id, true)}>
                                        <Check />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Reject">
                                      <IconButton color="error" size="small" onClick={() => handleJournalAction(entry.id, false)}>
                                        <CloseIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  <>
                                    {(!entry.tlFeedback || entry.tlFeedback.trim() === "") ? (
                                      <Tooltip title="Delete">
                                        <span>
                                          <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDeleteClick(entry.id)}
                                            disabled={false}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <span>
                                        <IconButton
                                          color="error"
                                          size="small"
                                          onClick={() => handleDeleteClick(entry.id)}
                                          disabled={true}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </span>
                                    )}
                                  </>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Fade>
            )}
          </Box>
        </Fade>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            No
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JournalListing;
